// @ts-check

export class FinstralCustomDisclosure extends HTMLElement {
	static #selectors = {
		disclosureToggle: ".js-FinstralCustomDisclosure-toggle",
		disclosureToggleLabel: ".js-FinstralCustomDisclosure-toggleLabel",
		itemsContainer: ".js-FinstralCustomDisclosure-itemsContainer",
	};

	static observedAttributes = ["label", "orientation"];

	#elements;
	#orientation = "horizontal";
	#showSelected = false;
	#closeOnSelect = true;
	#listenersBound = false;

	constructor() {
		super();

		// attributeChangeCallback happens _before_ connectedCallback,
		// so we need to cache the elements here to avoid race conditions.
		this.#elements = this.#getElements();
	}

	/**
	 * Retrieves the elements based on the defined selectors.
	 * @returns {{ disclosureToggle: HTMLElement | null, disclosureToggleLabel: HTMLElement | null, itemsContainer: HTMLElement | null }} An object containing the selected elements.
	 */
	#getElements() {
		return {
			disclosureToggle: /** @type {HTMLElement | null} */ (
				this.querySelector(FinstralCustomDisclosure.#selectors.disclosureToggle)
			),
			disclosureToggleLabel: /** @type {HTMLElement | null} */ (
				this.querySelector(
					FinstralCustomDisclosure.#selectors.disclosureToggleLabel,
				)
			),
			itemsContainer: /** @type {HTMLElement | null} */ (
				this.querySelector(FinstralCustomDisclosure.#selectors.itemsContainer)
			),
		};
	}

	connectedCallback() {
		this.#showSelected = this.hasAttribute("show-selected");
		this.#closeOnSelect = this.getAttribute("close-on-select") !== "false";
		if (this.hasAttribute("orientation")) {
			this.#orientation = /** @type {string} */ (
				this.getAttribute("orientation")
			);
		}
		this.#elements = this.#getElements();
		const { disclosureToggle, disclosureToggleLabel } = this.#elements;
		if (!disclosureToggle || !disclosureToggleLabel) {
			return;
		}

		if (this.#showSelected) {
			this.#setSelectedLabel();
		}

		if (!this.#listenersBound) {
			this.#addEventListeners();
			this.#listenersBound = true;
		}
	}

	/**
	 * Called when one of the observed attributes is changed.
	 * @param {string} name - The name of the changed attribute.
	 * @param {string} _oldValue - The previous value of the attribute.
	 * @param {string} newValue - The new value of the attribute.
	 */
	attributeChangedCallback(name, _oldValue, newValue) {
		if (name === "label") {
			const { disclosureToggleLabel, disclosureToggle } = this.#elements;
			if (!disclosureToggleLabel || !disclosureToggle) {
				return;
			}
			disclosureToggle.title = newValue;
			disclosureToggleLabel.textContent = newValue;
		}

		if (name === "orientation" && newValue) {
			this.#orientation = newValue;
		}
	}

	/**
	 * Checks if the disclosure toggle is expanded.
	 * @returns {boolean} True if expanded, otherwise false.
	 */
	#getIsExpanded() {
		const { disclosureToggle } = this.#elements;
		if (!disclosureToggle) {
			return false;
		}
		return disclosureToggle.getAttribute("aria-expanded") === "true";
	}

	/**
	 * Toggles the state of the disclosure drawer and prevents scrolling.
	 * @param {boolean} isExpanded - Indicates whether the drawer is currently expanded.
	 */
	#toggleDrawer(isExpanded) {
		const { disclosureToggle } = this.#elements;
		if (!disclosureToggle) {
			return;
		}
		disclosureToggle.setAttribute("aria-expanded", String(!isExpanded));
		disclosureToggle.classList.toggle("u-modal-open");
	}

	// TODO: This is currently only used by the LanguageSelect component.
	// We need to expand this so any component using this parent component can use it.
	// Also, we need to add support for horizontal orientation.
	#adjustDrawerPosition() {
		// Only called after a toggle click, so the toggle exists here.
		const itemsContainer = /** @type {HTMLElement} */ (
			this.#elements.itemsContainer
		);
		const disclosureToggle = /** @type {HTMLElement} */ (
			this.#elements.disclosureToggle
		);

		if (this.#orientation === "vertical") {
			const blockBuffer = 24;
			const distanceFromBottom =
				document.documentElement.clientHeight -
				disclosureToggle.getBoundingClientRect().bottom;
			const itemsContainerBlockSize = parseInt(
				getComputedStyle(itemsContainer).blockSize,
				10,
			);

			if (distanceFromBottom < itemsContainerBlockSize + blockBuffer) {
				itemsContainer.classList.add("flip-block");
			}
		}
	}

	/**
	 * Handles keyboard events for the disclosure toggle.
	 * @param {KeyboardEvent} event - The keyboard event object.
	 */
	#handleKeyboardEvents(event) {
		const { disclosureToggle } = this.#elements;
		const isExpanded = this.#getIsExpanded();

		if (event.key === "Escape" && isExpanded) {
			this.#toggleDrawer(isExpanded);
			// isExpanded is only true when the toggle exists.
			/** @type {HTMLElement} */ (disclosureToggle).focus();
		}
	}

	/**
	 * Handles click events outside the component to close the disclosure drawer.
	 * @param {MouseEvent} event - The click event object.
	 */
	#clickOutside(event) {
		const isExpanded = this.#getIsExpanded();
		if (!this.contains(/** @type {Node} */ (event.target)) && isExpanded) {
			this.#toggleDrawer(isExpanded);
		}
	}

	/**
	 * Collapses all other disclosure drawers on the page.
	 */
	#collapseOthers() {
		const others = Array.from(
			document.querySelectorAll("finstral-custom-disclosure"),
		).filter((disclosure) => disclosure !== this);

		others.forEach((disclosure) => {
			const disclosureToggle = disclosure.querySelector(
				FinstralCustomDisclosure.#selectors.disclosureToggle,
			);

			if (!disclosureToggle) {
				return;
			}

			disclosureToggle.setAttribute("aria-expanded", "false");
			disclosureToggle.classList.remove("u-modal-open");
		});
	}

	/**
	 * Sets the selected label for the disclosure toggle.
	 * @param {string} [label] - The label to set.
	 */
	#setSelectedLabel(label = "") {
		const labelFromAttribute = this.getAttribute("show-selected");
		const storedLabel = labelFromAttribute
			? sessionStorage.getItem(labelFromAttribute)
			: null;
		/** @type {string | null} */
		const currentLabel = label || storedLabel;
		const { disclosureToggleLabel } = this.#elements;

		if (!currentLabel || !disclosureToggleLabel) {
			return;
		}

		/** @type {HTMLElement} */
		disclosureToggleLabel.textContent = currentLabel;
	}

	/**
	 * Adds event listeners for the disclosure toggle and document interactions.
	 */
	#addEventListeners() {
		const { disclosureToggle } = this.#elements;
		if (!disclosureToggle) {
			return;
		}
		disclosureToggle.addEventListener("click", (event) => {
			event.stopPropagation();

			this.#collapseOthers();
			this.#toggleDrawer(this.#getIsExpanded());

			if (this.hasAttribute("as-popover")) {
				requestAnimationFrame(() => {
					this.#adjustDrawerPosition();
				});
			}
		});

		document.addEventListener("click", this.#clickOutside.bind(this));
		document.addEventListener("keyup", this.#handleKeyboardEvents.bind(this));
		if (this.#showSelected) {
			this.addEventListener("store-selected", (event) => {
				const customEvent = /** @type {CustomEvent} */ (event);

				if (!customEvent.detail) {
					return;
				}

				sessionStorage.setItem(
					customEvent.detail.key,
					customEvent.detail.value,
				);
				this.#setSelectedLabel(customEvent.detail.value);

				// Auto-close drawer after selection (opt-out via close-on-select="false")
				if (this.#closeOnSelect && this.#getIsExpanded()) {
					this.#toggleDrawer(true);
				}
			});
		}
	}
}

customElements.define("finstral-custom-disclosure", FinstralCustomDisclosure);
